import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { Status } from '../../../../../../../shared/builds/types';
import * as DependenciesV2Store from '../../../../../../redux/dependenciesV2';
import { getStatusFromDependencyState } from '../helpers';
import type { DependencyState } from '../../../../../../../shared/dependenciesV2';

import BuildStatePanel from './BuildStatePanel';
import FailedStatePanel from './FailedStatePanel';
import LoadingPanel from './LoadingPanel';
import UnknownStatePanel from './UnknownStatePanel';

type Props = {
  locator: string;
  status: DependencyState;
  setInProgressBuildId: (buildId: number | null) => void;
};

const StateContextPanel: VoidFunctionComponent<Props> = ({
  locator,
  status,
  setInProgressBuildId,
}) => {
  const revisionInstance = useSelector(DependenciesV2Store.selectors.getBuildWatchRevision(locator));
  const latestBuild = useSelector(DependenciesV2Store.selectors.getLatestBuildOnRevision(locator));
  const statusState = getStatusFromDependencyState(status);

  if (statusState === Status.Analyzed) {
    return null;
  }

  // If we don't have the revision in the store, we're probably still loading it,
  // but if we've already hit a terminal state, we should fall back to the usual rendering.
  if (!revisionInstance && ![Status.Unknown, Status.Failed].includes(statusState)) {
    return <LoadingPanel />;
  }

  return (
    <>
      {statusState === Status.Failed
        && <FailedStatePanel error={status.error} />}
      {statusState === Status.InProgress
        && <BuildStatePanel buildId={latestBuild?.id ?? null} setBuildId={setInProgressBuildId} />}
      {statusState === Status.Unknown && <UnknownStatePanel revision={locator} />}
    </>
  );
};

export default StateContextPanel;
